<template>
  <div>
    <AssignTable
      :headers="headers"
      :items="incidentActions"
      @assign="openAssignModal"
      @click:row="onClickItem"
      @unassign="openUnassignModal"
    >
      <template v-slot:action="{ item }">
        <v-btn @click.stop="openUnassignModal(item)" icon v-if="item.status !== 'act'">
          <v-icon small>
            mdi-link-variant-on
          </v-icon>
        </v-btn>
        <div v-else/>
      </template>

      <template #buttons>
        <v-speed-dial
          absolute
          bottom
          class="speed-dial-button"
          direction="top"
          right
          transition="slide-y-reverse-transition"
          v-model="fab"
        >
          <template v-slot:activator>
            <v-btn
              color="primary"
              dark
              fab
              v-model="fab"
            >
              <v-icon v-if="fab">mdi-close</v-icon>
              <v-icon v-else>fal fa-chevron-circle-up</v-icon>
            </v-btn>
          </template>
          <v-btn
            @click.stop="openAssignModal"
            color="blue darken-2"
            dark
            fab
            small
          >
            <v-icon> mdi-link-variant-plus</v-icon>
          </v-btn>
          <v-btn
            @click.stop="openActionModal"
            color="#7CB342"
            dark
            fab
            small
          >
            <v-icon> mdi-plus</v-icon>
          </v-btn>
        </v-speed-dial>
      </template>

    </AssignTable>

    <v-dialog v-model="modal.assigned" width="460">
      <AssignForm :items="assignableActions"
                  :title="$t('incidents.actions.assign_modal_title')"
                  :value="selected"
                  @cancel="onCancel"
                  @submit="onAssign"
      />
    </v-dialog>

    <v-dialog v-model="modal.unassigned" width="460">
      <UnassignForm
        :content="unassignedContentString"
        :title="$t('incidents.actions.unassigned_modal_title')"
        @cancel="onCancel"
        @submit="onUnassign"
      />
    </v-dialog>

    <v-dialog v-model="modal.addAction" width="450">
      <CreateFormAction
        :action="selectedAction"
        :incidents="[]"
        :priorities="priorities"
        :sites="[]"
        :workplaces="[]"
        :workstations="[]"
        :users="users"
        @cancel="onCancel"
        @submit="onCreateAndAssign"
        kind="assign"
      />
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import AssignTable from '@/common/assign/AssignTable.vue';
import AssignForm from '@/common/assign/assignForm.vue';
import UnassignForm from '@/common/assign/UnassignForm.vue';
import SectorsFiltering from '@/mixins/SectorsFiltering';
import CreateFormAction from '@/views/app/actions/_form.vue';
import actionFactory from '@/core/actionFactory';
import { assign } from 'lodash';
import prioritiesMixin from '@/views/app/actions/mixins/prioritiesMixin';

export default {
    name: 'ActionsTable',
    components: {
        UnassignForm,
        AssignForm,
        AssignTable,
        CreateFormAction,
    },
    mixins: [SectorsFiltering, prioritiesMixin],
    props: {
        actions: {
            required: true,
            type: Array,
        },
        incidentActions: {
            required: true,
            type: Array,
        },
        currentIncident: {
            require: true,
            type: Object,
        },
    },
    data() {
        return {
            selectedAction: actionFactory(),
            defaultAction: actionFactory(),
            fab: false,
            modal: {
                assigned: false,
                unassigned: false,
                addAction: false,
            },
            value: {},
            selected: [],
            headers: [
                {
                    text: this.$t('actions.status'),
                    sortable: true,
                    value: 'status',
                }, {
                    text: this.$t('common.title'),
                    sortable: true,
                    value: 'title',
                }, {
                    text: this.$t('actions.priority'),
                    sortable: true,
                    value: 'priority',
                }, {
                    text: this.$t('actions.planned_at'),
                    sortable: true,
                    value: 'planned_at',
                }, {
                    text: this.$t('actions.created_at'),
                    sortable: true,
                    value: 'created_at',
                }, {
                    text: this.$t('actions.updated_at'),
                    sortable: true,
                    value: 'updated_at',
                }],
        };
    },
    methods: {
        onCreateAndAssign(payload) {
            this.$http.post(`companies/${this.$store.getters.getCurrentCompanyID}/actions`, payload)
                .catch((e) => {
                    console.error(e);
                    this.snack({
                        text: this.$t('actions.errors.create_error_msg'),
                        color: 'error',
                    });
                })
                .then(response => this.$http.put(
                    `/companies/${this.$store.getters.getCurrentCompanyID}/themes/${this.currentIncident.theme.id}/incidents/${this.currentIncident.id}/actions/${response.data.id}`, // eslint-disable-line max-len
                ))
                .catch((e) => {
                    console.error(e);
                    this.snack({
                        text: this.$t('incidents.actions.errors.assigned_msg'),
                        color: 'error',
                    });
                })
                .finally(() => {
                    this.$store.dispatch('actions/flushActions');
                    this.selectedAction = assign(this.selectedAction,
                        { incidents: [this.currentIncident] },
                        this.getIncidentLowerSector());
                    this.$emit('update');
                    this.fab = false;
                    this.modal.addAction = false;
                });
        },
        ...mapActions('actions', ['setCurrentAction']),
        onCancel() {
            this.modal = {
                assigned: false,
                unassigned: false,
                addAction: false,
            };
            this.fab = false;
        },
        onAssign(payload) {
            return Promise.all(payload.map(id => this.axios.put(
                `/companies/${this.$store.getters.getCurrentCompanyID}/themes/${this.currentIncident.theme.id}/incidents/${this.currentIncident.id}/actions/${id}`,
            )))
                .then(() => {
                    this.modal.assigned = false;
                })
                .then(() => {
                    this.selected = [];
                })
                .then(() => this.$emit('update'))
                .catch((e) => {
                    console.error(e);
                    this.snack({
                        text: this.$t('incidents.actions.errors.assigned_msg'),
                        color: 'error',
                    });
                });
        },
        onUnassign() {
            if (this.value.id !== undefined) {
                return this.axios.delete(
                    `/companies/${this.$store.getters.getCurrentCompanyID}/themes/${this.currentIncident.theme.id}/incidents/${this.currentIncident.id}/actions/${this.value.id}`,
                )
                    .then(() => {
                        this.modal.unassigned = false;
                    })
                    .then(() => {
                        this.value = {};
                    })
                    .then(() => this.$emit('update'))
                    .catch((e) => {
                        console.error(e);
                        this.snack({
                            text: this.$t('incidents.actions.errors.unassigned_msg'),
                            color: 'error',
                        });
                    });
            }
            return Promise.resolve();
        },
        onClickItem(payload) {
            if (this.$store.getters.isGod) {
                return this.redirectToAction(payload);
            }
            if (this.currentUserActions.findIndex(action => action.id === payload.id) >= 0) {
                return this.redirectToAction(payload);
            }
            return false;
        },
        redirectToAction(payload) {
            this.setCurrentAction({ ...payload });
            this.$router.push(`/app/actions/${payload.id}`);
        },
        openAssignModal() {
            this.modal.assigned = true;
        },
        openActionModal() {
            this.selectedAction = assign(this.selectedAction,
                { incidents: [this.currentIncident] },
                this.getIncidentLowerSector());
            this.modal.addAction = true;
        },
        getIncidentLowerSector() {
            if (this.currentIncident.theme.workstation) {
                // eslint-disable-next-line @typescript-eslint/camelcase
                return { workstation_id: this.currentIncident.theme.workstation.id };
            }
            if (this.currentIncident.theme.workplace) {
                // eslint-disable-next-line @typescript-eslint/camelcase
                return { workplace_id: this.currentIncident.theme.workplace.id };
            }
            // eslint-disable-next-line @typescript-eslint/camelcase
            return { site_id: this.currentIncident.theme.site.id };
        },
        openUnassignModal(payload) {
            if (this.$store.getters.isGod
          || this.currentUserActions.findIndex(action => action.id === payload.id) >= 0) {
                this.value = payload;
                this.modal.unassigned = true;
            }
        },
    },
    computed: {
        assignedActionsID() {
            return this.incidentActions.map(i => i.id);
        },
        assignableActions() {
            if (this.$store.getters.isGod) {
                return this.actions.filter(i => !this.assignedActionsID.includes(i.id) && i.status !== 'act');
            }
            return this.currentUserActions.filter(i => !this.assignedActionsID.includes(i.id) && i.status !== 'act');
        },
        unassignedContentString() {
            return this.$t('incidents.actions.unassigned_modal_body', {
                action: this.value.title,
                incident: this.currentIncident.title,
            });
        },
        ...mapState('locations', {
            sites: state => state.sites,
            workplaces: state => state.workPlaces,
            workstations: state => state.workStations,
        }),
        ...mapState('entities', {
            users: state => state.users.map(u => ({
                ...u,
                name: `${u.first_name} ${u.last_name}`,
            })),
        }),
    },
    mounted() {
        if (!this.$store.getters.isGod) {
            this.fetchCurrentUserSectors()
                .catch(() => {
                    this.sectors = [];
                })
                .then(() => this.extractCurrentUserActions())
                .catch(() => {
                    this.currentUserActions = [];
                });
        }
    },
};
</script>

<style scoped>
  .speed-dial-button {
    bottom: -8%;
  }
</style>
